import type { ReactNode } from 'react';
import React from 'react';

import { Divider } from 'src/app/components/icons/divider';
import { DutchieLogo } from 'src/app/components/icons/dutchie-logo';
import { PlainCheck } from 'src/app/components/icons/plain-check';
import { Skip } from 'src/app/components/icons/skip';
import { Button } from 'src/app/components/lib/button';

import {
  NavigationContainer,
  Circle,
  StepsContainer,
  Step,
  Title,
  StepTitle,
  TitlesContainer,
  ButtonsContainer,
} from './wizard.styles';

import type { WizardProps } from '.';
import type { StepStatus } from './utils';

type TopNavigationProps = Pick<WizardProps, 'loading' | 'steps'> & {
  activeStep: number;
  enableNavigationByClicks: boolean;
  exitButtonLabel: string;
  onExit: () => void;
  onSave?: () => Promise<void>;
  onStepClick: (step: number) => void;
  saveAndExitDisabled: boolean | undefined;
  showSteps: boolean;
  stepStatusMap: Record<number, StepStatus>;
  subtitle?: ReactNode;
  title?: React.ReactElement | string;
  titleFontSize?: string;
  titleMinWidth?: string;
};

export function TopNavigation({
  activeStep,
  saveAndExitDisabled,
  exitButtonLabel,
  stepStatusMap,
  loading,
  onExit,
  onSave,
  showSteps,
  steps,
  subtitle,
  title,
  titleFontSize,
  titleMinWidth,
  enableNavigationByClicks,
  onStepClick,
}: TopNavigationProps) {
  return (
    <NavigationContainer $isHeader>
      <TitlesContainer>
        {title ? (
          <Title $titleFontSize={titleFontSize} $titleMinWidth={titleMinWidth}>
            {title}
          </Title>
        ) : (
          <DutchieLogo color='var(--color-opal-90)' />
        )}
        {subtitle}
      </TitlesContainer>
      {showSteps && (
        <StepsContainer>
          {Object.entries(stepStatusMap).map(([step, status]) => {
            const isActive = Number(step) === activeStep;
            const isLastStep = Number(step) === steps.length - 1;
            const showSkip = status.disabled;
            const showCheckmark = status.complete && !isActive && !showSkip;
            const showNumber = !showCheckmark && !showSkip;
            const isClickable = enableNavigationByClicks && showCheckmark;

            return (
              <React.Fragment key={step}>
                <Step $isClickable={isClickable} onClick={() => isClickable && onStepClick(Number(step))}>
                  <Circle $isActive={isActive} $isComplete={showCheckmark} $isDisabled={status.disabled}>
                    <>
                      {showNumber && Number(step) + 1}
                      {showCheckmark && <PlainCheck />}
                      {showSkip && <Skip size={11.5} />}
                    </>
                  </Circle>
                  <StepTitle $disabled={status.disabled}>{steps[Number(step)]?.title}</StepTitle>
                </Step>
                {!isLastStep && <Divider />}
              </React.Fragment>
            );
          })}
        </StepsContainer>
      )}
      <ButtonsContainer>
        <Button
          disabled={loading || (saveAndExitDisabled !== undefined && saveAndExitDisabled)}
          label={exitButtonLabel}
          variant='white'
          onClick={onExit}
        />
        {onSave && <Button disabled={loading} label='Save' variant='primary' onClick={onSave} />}
      </ButtonsContainer>
    </NavigationContainer>
  );
}
